import { Sites } from '@ps/platform-message-framework/common/Sites';
import { Currency, FeatureFlag } from '@ps/web-core-enums';
import type { TranslationTag } from '@ps/web-core-intl';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { RenderNames } from '@swf/casino';
import type { FeatureVariant } from '@swf/casino';
import { select } from '@swf/core/store';
import type { CasinoTable } from '@swf/core/types/CasinoGame';
import type { CasinoGameList, ShowField } from '@swf/core/types/CasinoGameList';

import {
  TXTACL_Bonus_Features,
  TXTACL_Maximum_Bet,
  TXTACL_Min_Bet,
  TXTACL_Minimum_Bet,
  TXTACL_Paylines,
  TXTACL_Return_to_Player,
  TXTMOB_Max_Bet,
  TXTWCL_Bonus_Round,
  TXTWCL_Free_Spins,
  TXTWCL_Jackpot
} from './EligibleFeatures.messages.json';
import FeatureDetails, { BLANK_VALUE, FormatType } from './FeatureDetails';

export interface RenderParams {
  tags: string[] | undefined;
  siteId: Sites;
  shortText?: boolean;
}

export interface RenderResult {
  title: TranslationTag;
  format: FormatType;
  currencyDigits?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
  inline?: boolean;
}

export interface Props {
  siteId: Sites;
  data: CasinoTable;
  className?: string;
  showFields?: CasinoGameList['renderInfo'];
  featureVariant?: FeatureVariant[];
  tags?: string[];
  shortText?: boolean;
  setBetFeatureCount(count: number): void;
}

export type RenderFunction = (table: CasinoTable, params: RenderParams) => RenderResult;

const bonusFeatures: Record<string, TranslationTag> = {
  pgjkpt: TXTWCL_Jackpot,
  bnsRnd: TXTWCL_Bonus_Round,
  fsbTitle: TXTWCL_Free_Spins
};

const paylines = {
  Megaways: 'Megaways™',
  ClusterPays: 'Cluster Pays'
};

const getPayline = (value: string, tags: string[] = []) =>
  Object.entries(paylines).reduce(
    (acc: string, [key, entryValue]) => (tags.indexOf(key) !== -1 ? entryValue : acc),
    value
  );

interface RenderField {
  flag?: FeatureFlag;
  fn: (table: CasinoTable, params: RenderParams) => RenderResult | null;
}

const RenderFields: Record<ShowField, RenderField> = {
  minBet: {
    flag: FeatureFlag.CasinoGameFeatureMinBet,
    fn: (table: CasinoTable, params: RenderParams) => {
      const omit = [Sites.SPA, Sites.CA_ON].includes(params.siteId);
      return omit
        ? null
        : {
            title: params.shortText ? TXTACL_Min_Bet : TXTACL_Minimum_Bet,
            value: table.minBet === 0 ? BLANK_VALUE : table.minBet,
            currencyDigits: table.minBet % 100 === 0 ? 0 : 2,
            format: FormatType.Currency
          };
    }
  },
  maxBet: {
    flag: FeatureFlag.CasinoGameFeatureMaxBet,
    fn: (table: CasinoTable, params: RenderParams) => {
      const omit = [Sites.SPA, Sites.CA_ON].includes(params.siteId);
      return omit
        ? null
        : {
            title: params.shortText ? TXTMOB_Max_Bet : TXTACL_Maximum_Bet,
            value: table.maxBet === 0 ? BLANK_VALUE : table.maxBet,
            currencyDigits: table.maxBet % 100 === 0 ? 0 : 2,
            format: FormatType.Currency
          };
    }
  },
  returnToPlayer: {
    flag: FeatureFlag.CasinoGameFeatureRTP,
    fn: (table: CasinoTable, _params: RenderParams) => ({
      title: TXTACL_Return_to_Player,
      value: table.properties?.holdRate ? 100 - table.properties.holdRate / 1e5 : 0,
      format: FormatType.Percentage
    })
  },
  paylines: {
    flag: FeatureFlag.CasinoGameFeaturePaylines,
    fn: (table: CasinoTable, params: RenderParams) => ({
      title: TXTACL_Paylines,
      value: getPayline(table.properties?.lines || '', params.tags),
      format: FormatType.String
    })
  },
  features: {
    flag: FeatureFlag.CasinoGameFeatureBonusFeatures,
    fn: (table: CasinoTable, _params: RenderParams) => ({
      title: TXTACL_Bonus_Features,
      value: table.featureNames?.length
        ? table.featureNames.map((feature) => bonusFeatures[feature])
        : 0,
      format: FormatType.Translation
    })
  }
};

const eligibleFeatures = (showFields?: CasinoGameList['renderInfo']) =>
  Object.keys(RenderFields)
    .filter((key) => (showFields || Object.values(RenderNames)).includes(key as ShowField))
    .map((key) => RenderFields[key as ShowField]);

const EligibleFeatures: React.FC<Props> = (props) => {
  const {
    setBetFeatureCount,
    data,
    className,
    featureVariant,
    showFields,
    siteId,
    shortText,
    tags
  } = props;
  const { currency } = data;
  const features = useSelector(select.feature.get);

  const toUseFeatures = Object.values(eligibleFeatures(showFields));

  const results = toUseFeatures
    .filter(
      (obj) =>
        (obj.flag ? !!features[obj.flag as keyof typeof features] : true) &&
        (!showFields ? !!obj.fn(data, { siteId, tags })?.value : true)
    )
    .map((feature, index) => {
      const detail = feature.fn(data, { siteId, tags, shortText });
      if (detail === null) {
        return null;
      }
      const processed =
        showFields && detail.format === FormatType.Translation && !detail.value
          ? { ...detail, value: '-', format: FormatType.String }
          : { ...detail };
      return (
        <FeatureDetails
          className={className}
          variant={featureVariant}
          key={index}
          {...processed}
          currency={data.playMoney ? Currency.PlayMoney : currency}
          currencyDigits={detail.currencyDigits}
        />
      );
    });

  useEffect(() => {
    setBetFeatureCount(results.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <>{results}</>;
};

export default EligibleFeatures;
